import { capitalizeWords } from '@/lib/utils';

const dateFormat = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta' });

const shortDateFormat = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' });

/** The parts of a regulation's identity that make up its citation. */
export interface CitationParts {
  type?: string | null;
  number?: string | null;
  year?: string | null;
}

/** "Undang-Undang No. 13 Tahun 2003", whichever parts exist. */
export function formatCitation({ type, number, year }: CitationParts): string {
  return [type ? capitalizeWords(type) : null, number ? `No. ${number}` : null, year ? `Tahun ${year}` : null]
    .filter(Boolean)
    .join(' ');
}

/**
 * "12 Oktober 2026" from a database timestamp, or null when it is missing or
 * unreadable. The date is shown in WIB whatever the viewer's own time zone is.
 *
 * @see parseDbTimestamp for how the stored text is read.
 */
export function formatDbDate(value: string | null | undefined): string | null {
  return formatWith(dateFormat, value);
}

/** "12 Okt" from a database timestamp, for places where the year is clear from context. */
export function formatShortDbDate(value: string | null | undefined): string | null {
  return formatWith(shortDateFormat, value);
}

/**
 * Reads a database timestamp as an instant, or null when it is unreadable.
 *
 * Database timestamps have a space instead of the "T", and timestamptz columns
 * end in a bare hour offset ("+00"); Safari and V8 reject both, so they are
 * normalised first. A timestamp without a zone is read as UTC: the platform's
 * database connection runs in UTC, so that is what it wrote. Reading it in the
 * viewer's own zone would move the date for anyone outside WIB.
 *
 * TODO(tech-debt): zoneless timestamp columns have no single write convention.
 * The platform pool writes UTC wall-clock (the database session zone is UTC),
 * while the shared pool (`timezone=Asia/Jakarta`) writes WIB wall-clock, so a
 * row written through the shared pool into the same column can show up to 7
 * hours ahead here. Reading zoneless values as UTC is only verified for the
 * columns used by the error-report and date-label features: consolidation
 * `last_updated_at` (saved by the platform), `enactedDate`, subscription
 * `endsAt` and the error-report timestamps. It is not a full fix: settle on one
 * convention (or move the columns to timestamptz) and audit the other writers.
 */
export function parseDbTimestamp(value: string): Date | null {
  const isoLike = value.includes('T') ? value : value.replace(' ', 'T');
  // Only after a time of day: a date-only value also ends in "-DD", which is not an offset.
  const withMinutes = isoLike.replace(/(\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?[+-]\d{2})$/, '$1:00');
  const hasTime = withMinutes.includes('T');
  const hasZone = /(Z|[+-]\d{2}:\d{2})$/.test(withMinutes);
  const date = new Date(hasTime && !hasZone ? `${withMinutes}Z` : withMinutes);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatWith(format: Intl.DateTimeFormat, value: string | null | undefined): string | null {
  if (!value) return null;
  const date = parseDbTimestamp(value);
  return date ? format.format(date) : null;
}
